import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Breadcrumb,
  Form,
} from "react-bootstrap";
import { useAuth } from "@clerk/clerk-react";
import { FaChartLine } from "react-icons/fa";
import createAxiosInstance from "../utility/utils";

function formatTimeTo12Hour(time24) {
  if (!time24) return "";
  let [hours, minutes] = time24.split(":");
  hours = parseInt(hours, 10);
  const suffix = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes}${suffix}`;
}

const AttendanceRecord = () => {
  const location = useLocation();
  const { getToken } = useAuth();

  const axiosInstance = useMemo(
    () => createAxiosInstance(getToken),
    [getToken]
  );

  // Destructure attendance session data from location.state
  const {
    sessionId,
    batchName,
    subjectName,
    date,
    time,
    attendanceRecords: initialRecords,
  } = location.state || {};

  const [isLoading, setIsLoading] = useState(true);
  const [presentCount, setPresentCount] = useState(0);
  const [absentCount, setAbsentCount] = useState(0);
  const [attendanceRecords, setAttendanceRecords] = useState(
    initialRecords || []
  );
  const [editingId, setEditingId] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  useEffect(() => {
    const fetchAttendanceRecords = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/attendance/session/${sessionId}`
        );
        if (response.data.success) {
          setAttendanceRecords(response.data.attendanceRecords);
        }
      } catch (error) {
        console.error("Error fetching updated attendance records", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (sessionId) {
      fetchAttendanceRecords();
    }
  }, [sessionId, axiosInstance]);

  useEffect(() => {
    const calculateCounts = () => {
      const present = attendanceRecords.filter(
        (record) => record.attendanceStatus === "present"
      ).length;
      const absent = attendanceRecords.length - present;
      setPresentCount(present);
      setAbsentCount(absent);
    };

    calculateCounts();
  }, [attendanceRecords]);

  const handleStatusChange = async (studentId, newStatus) => {
    try {
      setUpdateError(null);
      const response = await axiosInstance.post(
        "/api/attendance/bulk-update",
        {
          sessionId,
          updates: [
            {
              studentId,
              attendanceStatus: newStatus,
            },
          ],
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        const updatedRecords = attendanceRecords.map((record) =>
          record.student?._id === studentId
            ? { ...record, attendanceStatus: newStatus }
            : record
        );
        setAttendanceRecords(updatedRecords);
        setEditingId(null);
      } else {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (error) {
      setUpdateError(
        error.response?.data?.message || "Failed to update attendance"
      );
    }
  };

  const handleDownloadReport = async () => {
    setDownloadLoading(true);
    try {
      const params = new URLSearchParams();
      if (sessionId) params.append("sessionId", sessionId);
      if (date) params.append("date", date);
      if (subjectName) params.append("subjectName", subjectName);

      const response = await axiosInstance.get(
        `/api/attendance/download-report?${params.toString()}`,
        {
          responseType: "blob",
        }
      );

      // Create and trigger download
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fileName = `attendance_report_${date}_${subjectName.replace(
        /\s+/g,
        "_"
      )}.csv`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Failed to download report. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Breadcrumb>
            <Breadcrumb.Item href="/apps">Apps</Breadcrumb.Item>
            <Breadcrumb.Item href="/teachers">Teachers</Breadcrumb.Item>
            <Breadcrumb.Item active>Attendance Report</Breadcrumb.Item>
          </Breadcrumb>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">
              <FaChartLine className="me-2" />
              Attendance Report
            </h4>
            <Button
              variant="primary"
              onClick={handleDownloadReport}
              disabled={downloadLoading}
            >
              {downloadLoading ? "Downloading..." : "Download Report"}
            </Button>
          </div>

          <Row className="g-4">
            <Col md={6}>
              <div className="p-3 border rounded bg-light">
                <div className="mb-2">
                  <strong>Batch:</strong> {batchName}
                </div>
                <div className="mb-2">
                  <strong>Subject:</strong> {subjectName}
                </div>
                <div>
                  <strong>Date & Time:</strong> {date}{" "}
                  {formatTimeTo12Hour(time)}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="p-3 border rounded bg-light">
                <div className="d-flex justify-content-around">
                  <div className="text-center">
                    <h5 className="mb-1 text-success">{presentCount}</h5>
                    <div className="text-muted">Present</div>
                  </div>
                  <div className="text-center">
                    <h5 className="mb-1 text-danger">{absentCount}</h5>
                    <div className="text-muted">Absent</div>
                  </div>
                  <div className="text-center">
                    <h5 className="mb-1">{attendanceRecords.length}</h5>
                    <div className="text-muted">Total</div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Body>
          <Table responsive hover className="align-middle">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {attendanceRecords.map((record) => {
                const studentObj = record.student || {};
                const studentId = studentObj._id || `unknown-${Math.random()}`;
                const studentName = studentObj.name || "Unknown";

                return (
                  <tr key={studentId}>
                    <td>{studentName}</td>
                    <td>
                      {editingId === studentId ? (
                        <Form.Select
                          size="sm"
                          value={record.attendanceStatus}
                          onChange={(e) =>
                            handleStatusChange(studentId, e.target.value)
                          }
                          className="w-auto"
                        >
                          <option value="present">Present</option>
                          <option value="absent">Absent</option>
                        </Form.Select>
                      ) : (
                        <span
                          className={`badge ${
                            record.attendanceStatus === "present"
                              ? "bg-success"
                              : "bg-danger"
                          }`}
                        >
                          {record.attendanceStatus.toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td>
                      <Button
                        variant={
                          editingId === studentId ? "secondary" : "warning"
                        }
                        size="sm"
                        onClick={() =>
                          setEditingId(
                            editingId === studentId ? null : studentId
                          )
                        }
                      >
                        {editingId === studentId ? "Cancel" : "Edit"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {updateError && (
            <div className="alert alert-danger" role="alert">
              {updateError}
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AttendanceRecord;
